import { useEffect, useState } from "react";
import {
  Button,
  MenuItem,
  CircularProgress,
  Grid,
  InputLabel,
  FormControl,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import Select from "@mui/material/Select";
import { post, put } from "src/utils/networkUtils";
import useTenantCompetitions from "src/hooks/useTenantCompetitions";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { getEnabledLangs } from "src/langs/langs";
import LanguageTabs from "src/components/language/LanguageTabs";

const dateFormat = "YYYY-MM-DD HH:mm:ss";
const STAGE_PERIOD_LANGS = getEnabledLangs();
const STAGE_PERIOD_LANGUAGE_CODES = STAGE_PERIOD_LANGS.map((l) => l.value);

const defaultStagePeriod = {
  startDate: moment().format(dateFormat),
  endDate: moment().format(dateFormat),
  breakdown: "1=1000,2=700,3=500,4=400,5=300,6-10=200,11-30=150,31-100=100",
  isCompleted: false,
  isVisible: true,
  prizePool: "",
  name: "",
  nameI18n: {},
  description: "",
  descriptionI18n: {},
};

const isPlainObject = (value) =>
  !!value && typeof value === "object" && !Array.isArray(value);

const normalizeI18n = (rawI18n, fallbackEn, { stripUnknown = false } = {}) => {
  const preserved = isPlainObject(rawI18n) ? rawI18n : {};
  const next = stripUnknown ? {} : { ...preserved };

  for (const code of STAGE_PERIOD_LANGUAGE_CODES) {
    const raw = preserved?.[code];
    if (code === "en") {
      next[code] = raw ?? fallbackEn ?? "";
    } else {
      next[code] = raw ?? "";
    }
  }

  return next;
};

const normalizeStagePeriod = (incoming) => {
  const base = { ...defaultStagePeriod, ...(incoming ?? {}) };

  const rawNameI18n = isPlainObject(base.nameI18n) ? base.nameI18n : {};
  const rawDescriptionI18n = isPlainObject(base.descriptionI18n) ? base.descriptionI18n : {};

  const name = (base.name ?? "") || (rawNameI18n.en ?? "");
  const description = (base.description ?? "") || (rawDescriptionI18n.en ?? "");

  return {
    ...base,
    name,
    description,
    nameI18n: normalizeI18n(rawNameI18n, name),
    descriptionI18n: normalizeI18n(rawDescriptionI18n, description),
  };
};

const buildStagePeriodPayload = (stagePeriod) => {
  const payload = { ...(stagePeriod ?? {}) };

  const name = payload.name ?? "";
  const description = payload.description ?? "";

  if (isPlainObject(payload.nameI18n)) {
    payload.nameI18n = normalizeI18n(payload.nameI18n, name, { stripUnknown: true });
    const hasAny = Object.values(payload.nameI18n).some((v) => (v ?? "").toString().trim() !== "");
    if (!hasAny) delete payload.nameI18n;
  }

  if (isPlainObject(payload.descriptionI18n)) {
    payload.descriptionI18n = normalizeI18n(payload.descriptionI18n, description, { stripUnknown: true });
    const hasAny = Object.values(payload.descriptionI18n).some(
      (v) => (v ?? "").toString().trim() !== ""
    );
    if (!hasAny) delete payload.descriptionI18n;
  }

  return payload;
};
export default function StagePeriodDialog(props) {
  const [stagePeriod, setStagePeriod] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedLanguageTab, setSelectedLanguageTab] = useState(() => {
    const enIndex = STAGE_PERIOD_LANGS.findIndex((l) => l.value === "en");
    return enIndex >= 0 ? enIndex : 0;
  });

  // Use the hook to get tenant competitions
  const { tenantCompetitions } = useTenantCompetitions(props.tenantId);

  useEffect(() => {
    if (props?.stagePeriod) {
      setStagePeriod(normalizeStagePeriod(props.stagePeriod));
    } else {
      setStagePeriod(normalizeStagePeriod(defaultStagePeriod));
    }

    // Set default competition if tenant competitions are available
    if (tenantCompetitions.length > 0) {
      setStagePeriod((prevState) => ({
        ...prevState,
        competitionId: tenantCompetitions[0].competitionId,
      }));
    }
  }, [props.stagePeriod, tenantCompetitions]);
  const handleChange = (e) => {
    const { id, value } = e.target;

    setStagePeriod((prev) => {
      // Handle all i18n fields dynamically: "nameI18n.en", "nameI18n.fr", "descriptionI18n.fr", etc.
      if (id.includes(".")) {
        const [field, lang] = id.split(".");
        return {
          ...prev,
          [field]: { ...(prev[field] || {}), [lang]: value },
          
          // Keep legacy flat field (name / description) in sync with the primary language
          ...(field === "nameI18n" && { name: value }),
          ...(field === "descriptionI18n" && { description: value }),
        };
      }

      return { ...prev, [id]: value };
    });
  };

  const onSuccess = () => {
    setLoading(true);
    const tenantId = parseInt(props.tenantId) || 1;
    const payload = buildStagePeriodPayload({ ...stagePeriod, tenantId });
    if (props?.stagePeriod) {
      put(
        `/admin/stage-periods/${stagePeriod.stagePeriodId}?tenantId=${props.tenantId}`,
        payload
      ).then(function (response) {
        props.onSuccessHandler();
      });
    } else {
      post(`/admin/stage-periods?tenantId=${props.tenantId}`, payload).then(function (response) {
        props.onSuccessHandler();
      });
    }
  };
  const onCancel = () => {
    props.onClose();
  };

  const handleDateChange = (newValue, fieldName) => {
    console.log(newValue, moment(newValue).format(dateFormat));
    setStagePeriod((prevState) => ({
      ...prevState,
      [fieldName]: moment(newValue).seconds(0).format(dateFormat),
    }));
  };
  const handleCompetitionIdChange = (value) => {
    setStagePeriod((prevState) => ({
      ...prevState,
      competitionId: value,
    }));
  };
  const handleChangeCompleted = (e) => {
    setStagePeriod((prevState) => ({
      ...prevState,
      isCompleted: e.target.checked,
    }));
  };
  const handleChangeVisible = (e) => {
    setStagePeriod((prevState) => ({
      ...prevState,
      isVisible: e.target.checked,
    }));
  };
  console.log("stagePeriod edit", stagePeriod);
  const activeLanguage = STAGE_PERIOD_LANGS[selectedLanguageTab]?.value ?? "en";
  const activeLanguageLabel =
    STAGE_PERIOD_LANGS[selectedLanguageTab]?.label ?? activeLanguage.toUpperCase();
  return (
    <Dialog
      open={props.open}
      onClose={() => { }}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <DialogTitle id="alert-dialog-title">Stage Period</DialogTitle>
      <DialogContent>
        {stagePeriod === null || loading ? (
          <Box
            sx={{ display: "flex", justifyContent: "center" }}
            style={{ padding: 20 }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <Grid container>
            <Grid item xs={6} sx={{ m: 0, p: 1 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label="Start Date"
                  value={moment(stagePeriod.startDate, dateFormat)}
                  onChange={(value) => handleDateChange(value, "startDate")}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={6} sx={{ m: 0, p: 1 }}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label="End Date"
                  value={moment(stagePeriod.endDate, dateFormat)}
                  onChange={(value) => handleDateChange(value, "endDate")}
                  renderInput={(params) => <TextField {...params} />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sx={{ m: 0, p: 1 }}>
              <LanguageTabs
                langs={STAGE_PERIOD_LANGS}
                value={selectedLanguageTab}
                onChange={(_, newValue) => setSelectedLanguageTab(newValue)}
                ariaLabel="stage period language tabs"
                idPrefix="stage-period-language-tab"
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
              />
            </Grid>
            {/**
             * <Grid item xs={4} sx={{ m: 0, p: 1 }}>
              <TextField
                id="prizePool"
                label="Prize Pool"
                fullWidth
                value={stagePeriod.prizePool}
                variant="standard"
                onChange={handleChange}
              />
            </Grid>
             */}
            <Grid item xs={8} sx={{ m: 0, p: 1 }}>
              <TextField
                id={`nameI18n.${activeLanguage}`}
                label={`Name (${activeLanguageLabel})`}
                fullWidth
                value={stagePeriod.nameI18n?.[activeLanguage] ?? ""}
                variant="standard"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={4} sx={{ m: 0, p: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={stagePeriod.isVisible}
                    onChange={handleChangeVisible}
                    id="isVisible"
                  />
                }
                label="Visible"
              />
            </Grid>
            <Grid item xs={12} sx={{ m: 0, p: 1 }}>
              <TextField
                id={`descriptionI18n.${activeLanguage}`}
                label={`Description (${activeLanguageLabel})`}
                fullWidth
                value={stagePeriod.descriptionI18n?.[activeLanguage] ?? ""}
                variant="standard"
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sx={{ m: 0, p: 1 }}>
              <TextField
                id="breakdown"
                label="Breakdown"
                fullWidth
                value={stagePeriod.breakdown}
                variant="standard"
                onChange={handleChange}
              />
            </Grid>
            {/**
             * <Grid item xs={4} sx={{ m: 0, p: 1 }}>
              <TextField
                id="customPrizePool"
                label="Custom Prize Pool"
                fullWidth
                value={stagePeriod.customPrizePool}
                variant="standard"
                onChange={handleChange}
              />
            </Grid>*/}
            <Grid item xs={12} sx={{ m: 0, p: 1 }}>
              <TextField
                id="customPrizeBreakdown"
                label="Custom Prize Breakdown"
                fullWidth
                value={stagePeriod.customPrizeBreakdown}
                variant="standard"
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={4} sx={{ m: 0, p: 1 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={stagePeriod.isCompleted}
                    onChange={handleChangeCompleted}
                    id="isCompleted"
                  />
                }
                label="Completed"
              />
            </Grid>
            <Grid item xs={4} sx={{ m: 0, p: 1 }}>
              <TextField
                id="entryFee"
                label="Entry Fee Coins"
                fullWidth
                value={stagePeriod.entryFee}
                variant="standard"
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={4} sx={{ m: 0, p: 1 }}>
              <FormControl sx={{ m: 1, minWidth: 150 }} size="small">
                <InputLabel id="demo-simple-select-label">
                  Competition
                </InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  value={stagePeriod.competitionId ?? ""}
                  label="Competition"
                  onChange={(event) =>
                    handleCompetitionIdChange(event.target.value)
                  }
                >
                  {tenantCompetitions.length > 0 &&
                    tenantCompetitions.map((tenantCompetition) => (
                      <MenuItem
                        value={tenantCompetition.competitionId}
                        key={`competition-${tenantCompetition.competitionId}`}
                      >
                        {tenantCompetition.competitionName}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onCancel()}>Cancel</Button>
        <Button onClick={() => onSuccess()} autoFocus disabled={false}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}
